import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  isNotificationRoute,
} from 'dashboard/helper/routeHelpers';

const MAX_DISCONNECT_SECONDS = 10800;

// The disconnect delay threshold is added to account for delays in identifying
// disconnections (for example, the websocket disconnection takes up to 3 seconds)
// while fetching the latest updated conversations or messages.
const DISCONNECT_DELAY_THRESHOLD = 15;

// Latido de seguridad (Victor 2026-06-10): aunque la pestaña esté ACTIVA, el
// websocket puede quedar "zombi" (conectado pero sin recibir) sin disparar
// disconnect/reconnect. Cada HEARTBEAT_MS sincronizamos en silencio los mensajes
// del chat abierto (solo trae los nuevos, sin saltar el scroll); cada 3 latidos
// refrescamos también la lista. Así se ve en tiempo real sin refrescar a mano.
const HEARTBEAT_MS = 10000;

class ReconnectService {
  constructor(store, router) {
    this.store = store;
    this.router = router;
    this.disconnectTime = null;
    // Momento en que la pestaña pasó a segundo plano (para la recarga de
    // seguridad al volver a verla · Victor 2026-06-03).
    this.hiddenAt = null;
    this.heartbeatTick = 0;

    this.setupEventListeners();
    this.startHeartbeat();
  }

  disconnect = () => {
    this.removeEventListeners();
    this.stopHeartbeat();
  };

  // ── Latido invisible (Victor 2026-06-10) ──────────────────────────────
  startHeartbeat = () => {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(this.heartbeatSync, HEARTBEAT_MS);
  };

  stopHeartbeat = () => {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  };

  // Solo corre con la pestaña visible y en una vista de conversación. Sincroniza
  // en silencio los mensajes del chat abierto (cada 10s) y la lista (cada 30s).
  heartbeatSync = async () => {
    if (document.visibilityState !== 'visible') return;
    if (!isAConversationRoute(this.router.currentRoute.value.name, true)) return;

    this.heartbeatTick += 1;
    try {
      await this.fetchConversationMessagesOnReconnect();
      if (this.heartbeatTick % 3 === 0) {
        await this.fetchConversationsOnReconnect();
      }
    } catch (e) {
      // silencioso: el latido es best-effort, nunca debe romper la UI
    }
  };

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  // Recarga de seguridad al volver a la pestaña (Victor 2026-06-03): en segundo
  // plano el navegador "duerme" la pestaña y el websocket puede quedar zombi
  // (conectado pero sin recibir). Al volver a verla, recargamos lista + chat
  // abierto con la MISMA lógica que onReconnect, usando el tiempo que estuvo
  // oculta como ventana — así no se pierden mensajes sin tener que refrescar.
  handleVisibilityChange = async () => {
    if (document.visibilityState === 'hidden') {
      this.hiddenAt = new Date();
      return;
    }
    if (document.visibilityState !== 'visible' || !this.hiddenAt) return;
    const hiddenSince = this.hiddenAt;
    this.hiddenAt = null;
    // Si estuvo oculta muy poco, no vale la pena (evita spamear al cambiar de
    // pestaña rápido).
    if (Math.max(differenceInSeconds(new Date(), hiddenSince), 0) < 3) return;
    // Reusa la ventana de recarga de onReconnect sin pisar un disconnect real.
    const previousDisconnectTime = this.disconnectTime;
    this.disconnectTime = hiddenSince;
    try {
      await this.handleRouteSpecificFetch();
    } finally {
      this.disconnectTime = previousDisconnectTime;
    }
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  // Force reload if the user is disconnected for more than 3 hours
  handleOnlineEvent = () => {
    if (this.getSecondsSinceDisconnect() >= MAX_DISCONNECT_SECONDS) {
      window.location.reload();
    }
  };

  fetchConversations = async () => {
    await this.store.dispatch('updateChatListFilters', {
      page: null,
      updatedWithin:
        this.getSecondsSinceDisconnect() + DISCONNECT_DELAY_THRESHOLD,
    });
    await this.store.dispatch('fetchAllConversations');
    // Reset the updatedWithin in the store chat list filter after fetching conversations when the user is reconnected
    await this.store.dispatch('updateChatListFilters', {
      updatedWithin: null,
    });
  };

  fetchFilteredOrSavedConversations = async queryData => {
    await this.store.dispatch('fetchFilteredConversations', {
      queryData,
      page: 1,
    });
  };

  fetchConversationsOnReconnect = async () => {
    const {
      getAppliedConversationFiltersQuery,
      'customViews/getActiveConversationFolder': activeFolder,
    } = this.store.getters;
    const query = getAppliedConversationFiltersQuery?.payload?.length
      ? getAppliedConversationFiltersQuery
      : activeFolder?.query;
    if (query) {
      await this.fetchFilteredOrSavedConversations(query);
    } else {
      await this.fetchConversations();
    }
  };

  fetchConversationMessagesOnReconnect = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversationId),
      });
    }
  };

  fetchNotificationsOnReconnect = async filter => {
    await this.store.dispatch('notifications/index', { ...filter, page: 1 });
  };

  revalidateCaches = async () => {
    const { label, inbox, team } = await this.store.dispatch(
      'accounts/getCacheKeys'
    );
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
  };

  handleRouteSpecificFetch = async () => {
    const currentRoute = this.router.currentRoute.value.name;
    if (isAConversationRoute(currentRoute, true)) {
      await this.fetchConversationsOnReconnect();
      await this.fetchConversationMessagesOnReconnect();
    } else if (isAInboxViewRoute(currentRoute, true)) {
      await this.fetchNotificationsOnReconnect(
        this.store.getters['notifications/getNotificationFilters']
      );
    } else if (isNotificationRoute(currentRoute)) {
      await this.fetchNotificationsOnReconnect();
    }
  };

  setConversationLastMessageId = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  onDisconnect = () => {
    this.disconnectTime = new Date();
    this.setConversationLastMessageId();
  };

  onReconnect = async () => {
    await this.handleRouteSpecificFetch();
    await this.revalidateCaches();
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
  };
}

export default ReconnectService;
